import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  CircularProgress,
} from "@mui/material";
import { format, parseISO } from "date-fns";
import { useTranslation } from "react-i18next";
import addIcon from "../../assets/img/add.png";

interface AppointmentDialogProps {
  open: boolean;
  onClose: () => void;
  formData: any;
  setFormData: React.Dispatch<React.SetStateAction<any>>;
  submitAppointment: (_data: any) => void;
}

const AppointmentDialog: React.FC<AppointmentDialogProps> = ({
  open,
  onClose,
  formData,
  submitAppointment,
}) => {
  const { t } = useTranslation();
  const [descriptionError, setDescriptionError] = useState(false);
  const [loading, setLoading] = useState(false);

  const [title, setTitle] = useState(formData.title || "");
  const [description, setDescription] = useState(formData.description || "");

  useEffect(() => {
    if (open) {
      setTitle(formData.title || "");
      setDescription(formData.description || "");
      setDescriptionError(false);
      setLoading(false);
    }
  }, [open, formData]);

  const handleSubmit = async () => {
    const desc = description.trim();
    if (!desc) {
      setDescriptionError(true);
      return;
    }
    setDescriptionError(false);
    setLoading(true);
    try {
      await submitAppointment({
        ...formData,
        title,
        description: desc,
      });
      setLoading(false);
      onClose();
    } catch (error) {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: 4,
            boxShadow: 8,
            overflowX: "hidden",
            maxWidth: 500,
          },
        },
      }}
    >
      <DialogContent sx={{ pt: 4, px: 4 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mb: 3,
          }}
        >
          <Box
            sx={{
              width: 60,
              height: 60,
              borderRadius: "50%",
              backgroundColor: "#FFF8F0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box
              component="img"
              src={addIcon}
              alt="Add"
              sx={{ width: 58, height: 58 }}
            />
          </Box>
        </Box>

        <Typography
          variant="h5"
          align="center"
          sx={{
            fontWeight: 600,
            mb: 3,
            color: "#333",
          }}
        >
          {t("appointment.newAppointment")}
        </Typography>

        {formData.start && formData.end && (
          <Box
            sx={{
              p: 2,
              bgcolor: "#F2F9FF",
              borderRadius: 2,
              mb: 3,
              textAlign: "center",
            }}
          >
            <Typography
              variant="body1"
              sx={{ fontWeight: 500, color: "#0066CC" }}
            >
              {format(parseISO(formData.start), "EEEE, MMMM d, yyyy")}
            </Typography>
            <Typography
              variant="body1"
              sx={{ fontWeight: 600, color: "#0066CC", mt: 0.5 }}
            >
              {format(parseISO(formData.start), "h:mm a")} -{" "}
              {format(parseISO(formData.end), "h:mm a")}
            </Typography>
          </Box>
        )}

        <Typography sx={{ mb: 1, mt: 2, fontWeight: 500, color: "#555" }}>
          {t("appointment.information")}
        </Typography>

        <TextField
          placeholder={t("appointment.placeholderTile")}
          fullWidth
          margin="normal"
          value={title}
          variant="outlined"
          sx={{
            mb: 3,
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
            },
          }}
          onChange={(e) => setTitle(e.target.value)}
        />

        <Box sx={{ mb: 2 }}>
          <Typography
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 1,
              fontWeight: 500,
              color: "#555",
            }}
          >
            {t("appointment.description")}
            <Box component="span" sx={{ color: "error.main", ml: 0.5 }}>
              *
            </Box>
          </Typography>
          <TextField
            placeholder={t("appointment.placeholderDescription")}
            fullWidth
            margin="normal"
            value={description}
            variant="outlined"
            sx={{
              mt: 0,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
              },
            }}
            multiline
            minRows={2}
            required
            error={descriptionError}
            helperText={descriptionError ? "Description is required" : ""}
            onChange={(e) => {
              setDescriptionError(false);
              setDescription(e.target.value);
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions
        sx={{ p: 4, display: "flex", justifyContent: "space-between" }}
      >
        <Button
          variant="outlined"
          onClick={onClose}
          fullWidth
          sx={{
            borderRadius: 2,
            py: 1.5,
            fontSize: 16,
            textTransform: "none",
            mr: 1,
            backgroundColor: "#FEF2E9",
            color: "#E67C52",
            border: "none",
            fontWeight: "bold",
            "&:hover": {
              backgroundColor: "#FFE8E0",
              border: "none",
            },
          }}
          disabled={loading}
        >
          {t("appointment.cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          fullWidth
          sx={{
            textTransform: "none",
            borderRadius: 2,
            fontSize: 16,
            py: 1.5,
            ml: 1,
            height: 50,
            backgroundColor: "#DE6E1D",
            fontWeight: "bold",
            "&:hover": {
              backgroundColor: "#D95C2F",
            },
          }}
          disabled={loading}
        >
          {loading ? (
            <CircularProgress size={20} sx={{ color: "#fff" }} />
          ) : (
            t("button.submit")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AppointmentDialog;
